
import React, { useEffect, useState, useRef } from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import { generateDashboardBriefing } from '../services/geminiService';
import { IconQuant, IconActivity, IconArchitecture, IconTrendingUp } from '../components/Icons';
import { useAiQ } from '../state/AiQState';
import { usePortfolio } from '../../context/PortfolioContext';
import toast from 'react-hot-toast';

const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#6366f1'];

const DashboardView: React.FC = () => {
  const { portfolioStats, systemDate, runDayEndBatch, notifications } = useAiQ(); 
  const { activePortfolioSummary } = usePortfolio();
  const [briefing, setBriefing] = useState<string>('');
  const [loadingBriefing, setLoadingBriefing] = useState(false);
  const [isBatchRunning, setIsBatchRunning] = useState(false);
  
  // Flash Effects
  const [flashColor, setFlashColor] = useState('');
  const prevValRef = useRef(portfolioStats.totalValuation);

  useEffect(() => {
      if (portfolioStats.totalValuation > prevValRef.current) {
          setFlashColor('text-emerald-600 transition-colors duration-300');
      } else if (portfolioStats.totalValuation < prevValRef.current) {
          setFlashColor('text-red-600 transition-colors duration-300');
      }
      
      const timer = setTimeout(() => setFlashColor('text-slate-900 transition-colors duration-500'), 300);
      prevValRef.current = portfolioStats.totalValuation;
      
      return () => clearTimeout(timer);
  }, [portfolioStats.totalValuation]);

  // Format large numbers
  const formatValuation = (val: number) => {
      if (val > 1000000000) return `$${(val / 1000000000).toFixed(3)} B`; // 3 decimals for live movement
      if (val > 1000000) return `$${(val / 1000000).toFixed(3)} M`;
      return `$${val.toLocaleString()}`;
  };

  const handleRunBatch = async () => {
      setIsBatchRunning(true);
      toast.loading("Running End-of-Day Batch Process...", { duration: 2000 });
      await new Promise(r => setTimeout(r, 2000)); 
      await runDayEndBatch();
      setIsBatchRunning(false);
      toast.success("System Rolled Forward Successfully");
  };

  useEffect(() => {
    const fetchBriefing = async () => {
        if (briefing) return; // Only fetch once initially to avoid flickering
        setLoadingBriefing(true);
        
        try {
          const metrics = {
              date: systemDate,
              totalValuation: formatValuation(portfolioStats.totalValuation),
              dailyPnL: portfolioStats.dailyPnL > 0 ? `+${formatValuation(portfolioStats.dailyPnL)}` : formatValuation(portfolioStats.dailyPnL),
              tradeCount: portfolioStats.tradeCount,
              alerts: "Monitor: Live Market Data connected."
          };
          const text = await generateDashboardBriefing(metrics);
          setBriefing(text || `Portfolio valuation at ${metrics.totalValuation}. Daily P&L: ${metrics.dailyPnL}. ${metrics.tradeCount} active positions.`);
        } catch (error) {
          console.error("Failed to generate briefing:", error);
          // Fallback briefing if AI service fails
          setBriefing(`Portfolio valuation at ${formatValuation(portfolioStats.totalValuation)}. Daily P&L: ${portfolioStats.dailyPnL >= 0 ? '+' : ''}${formatValuation(portfolioStats.dailyPnL)}. ${portfolioStats.tradeCount} active positions.`);
        } finally {
          setLoadingBriefing(false);
        }
    };
    
    fetchBriefing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount, metrics captured in closure

  // Get real diversification data from portfolio summary
  const diversificationData = activePortfolioSummary?.diversification || [];
  
  // Get recent notifications (last 5)
  const recentNotifications = notifications.slice(0, 5);

  // Map severity colors
  const getSeverityColor = (type: string) => {
    switch(type) {
      case 'critical': return 'bg-red-100 text-red-700';
      case 'warning': return 'bg-amber-100 text-amber-700';
      case 'success': return 'bg-green-100 text-green-700';
      case 'audit': return 'bg-purple-100 text-purple-700';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  const getSeverityLabel = (type: string) => {
    return type.toUpperCase();
  };

  return (
    <div className="p-6 space-y-6 h-full overflow-y-auto custom-scrollbar">
      
      {/* Top Row: Briefing + System Control */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* AI Morning Briefing */}
          <div className="lg:col-span-2 bg-gradient-to-r from-slate-900 to-slate-800 rounded-lg p-6 shadow-lg text-white relative overflow-hidden border border-slate-700 flex flex-col justify-center">
             <div className="absolute right-0 top-0 h-full w-1/3 bg-blue-500/10 transform skew-x-12"></div>
             <div className="relative z-10">
                 <h2 className="text-sm font-bold text-blue-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <IconQuant /> 
                    AiQ Executive Briefing ({systemDate})
                 </h2>
                 <div className="min-h-[3rem]">
                    {loadingBriefing ? (
                        <div className="flex space-x-2 animate-pulse">
                            <div className="h-2 bg-slate-600 rounded w-12"></div>
                            <div className="h-2 bg-slate-600 rounded w-24"></div>
                            <div className="h-2 bg-slate-600 rounded w-full"></div>
                        </div>
                    ) : (
                        <p className="text-lg font-light leading-snug text-slate-100 font-serif italic">
                            "{briefing}"
                        </p>
                    )}
                 </div>
             </div>
          </div>

          {/* System Control Panel */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200 flex flex-col justify-between">
              <div className="flex justify-between items-start">
                  <div>
                      <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide flex items-center gap-2">
                          <IconActivity /> System Status
                      </h3>
                      <p className="text-xs text-emerald-600 font-bold mt-1 flex items-center gap-1">
                          <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                          LIVE PRICING ACTIVE
                      </p>
                  </div>
                  <div className="text-right">
                      <div className="text-xs text-slate-400 uppercase">System Date</div>
                      <div className="text-xl font-mono font-bold text-slate-700">{systemDate}</div>
                  </div>
              </div>
              <button 
                  onClick={handleRunBatch}
                  disabled={isBatchRunning}
                  className="mt-4 w-full py-2 bg-slate-800 text-white text-sm font-bold rounded hover:bg-slate-900 transition-all flex items-center justify-center gap-2 disabled:opacity-70"
              >
                  {isBatchRunning ? (
                      <>
                        <span className="w-4 h-4 border-2 border-slate-400 border-t-white rounded-full animate-spin"></span>
                        Processing...
                      </>
                  ) : (
                      <>
                        <IconArchitecture /> Run End-of-Day Batch
                      </>
                  )}
              </button>
          </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Key Metrics - LIVE */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
          <h3 className="text-slate-500 text-sm font-medium flex justify-between">
              Total Valuation
              <IconTrendingUp />
          </h3>
          <p className={`text-2xl font-bold mt-2 font-mono ${flashColor}`}>
              {formatValuation(portfolioStats.totalValuation)}
          </p>
          <span className="text-emerald-600 text-xs font-medium mt-1 inline-block">↑ Real-time Update</span>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
          <h3 className="text-slate-500 text-sm font-medium">Daily P&L (Est.)</h3>
          <p className={`text-2xl font-bold mt-2 font-mono ${portfolioStats.dailyPnL >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {formatValuation(portfolioStats.dailyPnL)}
          </p>
          <span className="text-slate-400 text-xs font-medium mt-1 inline-block">vs. Prev Close</span>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
          <h3 className="text-slate-500 text-sm font-medium">Active Trades</h3>
          <p className="text-2xl font-bold text-slate-900 mt-2">{portfolioStats.tradeCount}</p>
          <span className="text-slate-400 text-xs font-medium mt-1 inline-block">Consolidated Blotter</span>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
          <h3 className="text-slate-500 text-sm font-medium">Regulatory Status</h3>
          <p className="text-2xl font-bold text-emerald-600 mt-2">Compliant</p>
          <span className="text-slate-400 text-xs font-medium mt-1 inline-block">Last FRR Report: Today</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sector Diversification - REAL DATA */}
        {diversificationData.length > 0 ? (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Sector Diversification</h3>
            <div style={{ width: '100%', height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={diversificationData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry: any) => `${entry.sector}: ${entry.percentage?.toFixed(1)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="sector"
                  >
                    {diversificationData.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: any) => `$${(value || 0).toLocaleString()}`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Sector Diversification</h3>
            <div className="h-72 flex items-center justify-center">
              <p className="text-slate-400">No diversification data available. Add transactions to see sector breakdown.</p>
            </div>
          </div>
        )}

        {/* Top Holdings - REAL DATA */}
        {activePortfolioSummary?.pnl_by_ticker && activePortfolioSummary.pnl_by_ticker.length > 0 ? (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Top Holdings by Value</h3>
            <div className="space-y-3">
              {activePortfolioSummary.pnl_by_ticker
                .sort((a: any, b: any) => Math.abs(b.market_value) - Math.abs(a.market_value))
                .slice(0, 8)
                .map((position: any, index: number) => (
                  <div key={position.ticker} className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <span className="text-xs font-bold text-slate-400 w-6">{index + 1}</span>
                      <span className="font-mono font-bold text-slate-800">{position.ticker}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm font-bold text-slate-900">
                        ${(position.market_value || 0).toLocaleString()}
                      </div>
                      <div className={`text-xs font-medium ${position.total_pnl >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {position.total_pnl >= 0 ? '+' : ''}{position.total_pnl?.toFixed(0)} ({position.pnl_percentage?.toFixed(1)}%)
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ) : (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Top Holdings by Value</h3>
            <div className="h-72 flex items-center justify-center">
              <p className="text-slate-400">No positions available. Add transactions to see top holdings.</p>
            </div>
          </div>
        )}
      </div>
      
      {/* System Notifications - REAL DATA */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-slate-800">System Alerts & Notifications</h3>
          <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">View All</button>
        </div>
        {recentNotifications.length > 0 ? (
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="px-6 py-3 font-medium">Time</th>
                <th className="px-6 py-3 font-medium">Module</th>
                <th className="px-6 py-3 font-medium">Message</th>
                <th className="px-6 py-3 font-medium">Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {recentNotifications.map((notification) => (
                <tr key={notification.id}>
                  <td className="px-6 py-3 text-slate-600 font-mono text-xs">{notification.timestamp}</td>
                  <td className="px-6 py-3 text-slate-800 font-medium">{notification.module || 'System'}</td>
                  <td className="px-6 py-3 text-slate-600">{notification.message}</td>
                  <td className="px-6 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${getSeverityColor(notification.type)}`}>
                      {getSeverityLabel(notification.type)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="px-6 py-8 text-center">
            <p className="text-slate-400">No recent notifications. System is operating normally.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardView;
